import { type ProductFragment, useProductsQuery } from "@qw/graphql";
import { Column, ColumnContent, Icon, Input, Spinner } from "@qw/ui";
import { useMemo, useState } from "react";
import { ProductsCarrierSection } from "./products-section";

export const ProductsIndex = () => {
  const { data: productsData, loading: productsLoading } = useProductsQuery();
  const [searchQuery, setSearchQuery] = useState("");

  const loading = productsLoading;

  const products = useMemo(() => productsData?.products ?? [], [productsData]);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return products;
    }

    const query = searchQuery.toLowerCase();
    return products.filter((product) => {
      // Search by product name
      if (product.name?.toLowerCase().includes(query)) {
        return true;
      }

      // Search by carrier name
      if (product.carrier?.name?.toLowerCase().includes(query)) {
        return true;
      }

      // Search by line of business name
      if (product.linesOfBusiness?.some((lob) => lob.displayName?.toLowerCase().includes(query))) {
        return true;
      }

      return false;
    });
  }, [products, searchQuery]);

  const groupedByCarrier = useMemo(() => {
    const groups: Record<string, { name: string; carrierId: string | null; products: ProductFragment[] }> = {};

    // Assign filtered products to their carrier groups
    filteredProducts.forEach((product) => {
      const carrierSlug = product.carrier?.slug;
      const carrierName = product.carrier?.name;
      const carrierId = product.carrier?.id;

      if (!carrierSlug || !carrierName) {
        // Add to "Uncategorized" group for products without carrier
        if (!groups["uncategorized"]) {
          groups["uncategorized"] = { name: "Uncategorized", carrierId: null, products: [] };
        }
        groups["uncategorized"].products.push(product);
      } else {
        // Create carrier group if it doesn't exist
        if (!groups[carrierSlug]) {
          groups[carrierSlug] = { name: carrierName, carrierId: carrierId ?? null, products: [] };
        }
        groups[carrierSlug].products.push(product);
      }
    });

    // Sort products within each group alphabetically
    Object.values(groups).forEach((group) => {
      group.products.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    });

    return groups;
  }, [filteredProducts]);

  // Get sorted Carrier entries (by name), filtering out empty groups
  const sortedCarrierEntries = useMemo(() => {
    return Object.entries(groupedByCarrier)
      .filter(([, group]) => group.products.length > 0)
      .sort(([, a], [, b]) => a.name.localeCompare(b.name));
  }, [groupedByCarrier]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  if (loading && products.length === 0) {
    return (
      <div className="flex-auto flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4">
          <div className="text-center text-muted-foreground py-12">No products available.</div>
        </ColumnContent>
      </Column>
    );
  }

  return (
    <Column className="flex-auto bg-background flex flex-col">
      <div className="sticky top-0 z-10 p-4">
        <label className="relative w-full max-w-sm">
          <Icon icon="search" className="text-base absolute left-0 top-3.25 pointer-events-none w-9" />
          <span className="sr-only">Search products by name, carrier, or line-of-business</span>
          <Input
            placeholder="Search products by name, carrier, or line-of-business"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-8 py-2 rounded-full bg-muted border-0"
          />
        </label>
      </div>
      <ColumnContent className="p-4 space-y-1 flex-1 overflow-y-auto">
        {sortedCarrierEntries.map(([carrierSlug, group]) => (
          <ProductsCarrierSection
            key={carrierSlug}
            carrierName={group.name}
            carrierId={group.carrierId}
            products={group.products}
          />
        ))}
      </ColumnContent>
    </Column>
  );
};
